import * as React from 'react'
import { useState, useEffect, useRef } from 'react'
import {
  Grid,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  Container,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { EditOutlined } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { showSnackbar } from '@context/SnackbarContext'
import WebService from '@util/webService'
import { getAddress } from '@src/util/helper'
import useAlert from '@hooks/useAlert'
import FlatButton from '@components/FlatButton'
import MyDialog2 from '@components/MyDialog2'
import MyPagination from '@components/MyPagination'
import MobileLabel from '@components/MobileLabel'
import ConfirmDialog from '@components/ConfirmDialog'
import AddrPopup from '@components/AddrPopup'
import useIndicator from '@hooks/useIndicator'
import NumericTextField from '@components/NumericTextField'
import axios from 'axios'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import CloseIcon from '@mui/icons-material/Close'
import no_image from '@images/no_image.png'
const boldFont = {
  fontWeight: 'bold',
}

const defPageInfo = { pageNum: 0, pageSize: 10 }

// BranchesRow 전 컬럼을 채워서 전송: Gson 역직렬화 시 null 필드가 생기지 않도록 함
const newBranch = {
  id: -1,
  branchName: '',
  companyName: '',
  repName: '',
  bizNum: '',
  bizNum1: '',
  bizNum2: '',
  bizNum3: '',
  zipCode: '',
  addr1: '',
  addr2: '',
  phone: '',
  phoneDial: '',
  phone1: '',
  phone2: '',
  statCode: 'S000',
  displayYn: 'Y',
  hqYn: 'N',
  subLeaseYn: 'N',
  ownerName: '',
}

// 영업 중 지점 상태코드(B001): 인장 파일 필수
const STAT_OPEN = 'S001'

// 인장 첨부 용도(cat_code): 임대인 인장 / 소유주 인장
const CAT_STAMP1 = 'SP01'
const CAT_STAMP2 = 'SP02'
// 업로드 파일명 접두어: 서버(BranchController.catCodeOf)가 이 접두어로 용도를 구분함
const STAMP_PREFIX = { [CAT_STAMP1]: 'stamp1_', [CAT_STAMP2]: 'stamp2_' }

const attchUrl = (attch) => (attch ? `/bis/repo/${attch.fileLoc}` : no_image)

const onlyDigits = (v) => (v ?? '').replace(/\D/g, '')

const formatPhone = (b) => [b.phoneDial, b.phone1, b.phone2].filter((s) => s).join('-')

// 사업자번호 표시: 분할 컬럼이 모두 있으면 3-2-5 형식, 아니면 biz_num 원본
const formatBizNum = (b) => {
  if (b.bizNum1 && b.bizNum2 && b.bizNum3) {
    return `${b.bizNum1}-${b.bizNum2}-${b.bizNum3}`
  }
  return b.bizNum ?? ''
}

// 인장 파일 입력 1칸: 신규 선택 파일 또는 기존 첨부 중 하나를 표시
const StampField = ({ label, required, helperText, files, attchList, imgUrl, onChangeFile, onClear }) => {
  const hasFile = files.length > 0 || attchList.length > 0
  return (
    <Grid container spacing={2}>
      <Grid item xs={2} style={{ display: 'flex' }}>
        <img src={imgUrl} width='100' />
        {
          hasFile ? (
            <CloseIcon
              style={{ fontSize: '2em', cursor: 'pointer' }}
              onClick={onClear}
            />
          ) : null
        }
      </Grid>
      <Grid item xs={10}>
        <TextField
          variant='standard'
          type='text'
          value={files.length > 0 ? files[0].name : attchList.length > 0 ? attchList[0].name : ''}
          fullWidth
          required={required}
          label={label}
          helperText={helperText}
          InputLabelProps={{ shrink: hasFile }}
          InputProps={{
            endAdornment: (
              <IconButton component='label'>
                <AttachFileOutlinedIcon />
                <input
                  type='file'
                  accept='.png'
                  hidden
                  onChange={onChangeFile}
                />
              </IconButton>
            ),
          }}
        />
      </Grid>
    </Grid>
  )
}

// BranchList 밖에 정의: 부모 재렌더 시 입력 중인 폼 상태가 초기화되지 않도록 함
const BranchForm = ({ branch, onSubmit }) => {
  const theme = useTheme()
  const codes = useSelector((state) => state.codeReducer)
  const statCodes = codes.filter((r) => r.codeGroupId === 'B001')
  const dialCodes = codes.filter((r) => r.codeGroupId === 'C003')
  const [form, set_form] = useState({ ...newBranch, ...branch })
  const addrRef = useRef()
  const isNew = form.id === -1
  const setField = (field, value) => {
    set_form((prev) => ({ ...prev, [field]: value }))
  }
  // 기존 인장 첨부로 초기화: 비워 두면 저장 시 oldAttchIdList 가 비어 서버가 기존 인장을 삭제함
  const initAttchs1 = (branch.attchList ?? []).filter((a) => a.catCode === CAT_STAMP1)
  const initAttchs2 = (branch.attchList ?? []).filter((a) => a.catCode === CAT_STAMP2)
  // 임대인 인장(SP01)
  const [files1, set_files1] = useState([])
  const [attchList1, set_attchList1] = useState(initAttchs1)
  const [imgUrl1, set_imgUrl1] = useState(attchUrl(initAttchs1[0]))
  // 소유주 인장(SP02)
  const [files2, set_files2] = useState([])
  const [attchList2, set_attchList2] = useState(initAttchs2)
  const [imgUrl2, set_imgUrl2] = useState(attchUrl(initAttchs2[0]))
  const isSubLease = form.subLeaseYn === 'Y'
  const validate = () => {
    if (!form.branchName?.trim()) {
      return '지점명을 입력해 주세요.'
    }
    const phoneParts = [form.phoneDial, form.phone1, form.phone2]
    const filled = phoneParts.filter((s) => s).length
    if (filled > 0 && filled < 3) {
      return '전화번호를 모두 입력하거나 모두 비워 주세요.'
    }
    const bizParts = [form.bizNum1 ?? '', form.bizNum2 ?? '', form.bizNum3 ?? '']
    const bizFilled = bizParts.filter((s) => s).length
    if (bizFilled > 0) {
      if (bizParts[0].length !== 3 || bizParts[1].length !== 2 || bizParts[2].length !== 5) {
        return '사업자번호를 3-2-5 자리로 모두 입력하거나 모두 비워 주세요.'
      }
    }
    if (!form.statCode) {
      return '상태를 선택해 주세요.'
    }
    // 영업 중 지점은 계약서 임대인 인장으로 쓰이므로 인장 파일 필수 (새 파일 또는 기존 첨부)
    if (form.statCode === STAT_OPEN && files1.length === 0 && attchList1.length === 0) {
      return '영업 중인 지점은 인장 파일을 등록해 주세요.'
    }
    // 전대(소유주와 임대인이 다른 경우)는 계약서 소유주 동의란에 쓰이므로 소유주명·소유주 인장 필수
    if (isSubLease) {
      if (!form.ownerName?.trim()) {
        return '소유주와 임대인이 다를 경우 소유주명을 입력해 주세요.'
      }
      if (files2.length === 0 && attchList2.length === 0) {
        return '소유주와 임대인이 다를 경우 소유주 인장 파일을 등록해 주세요.'
      }
    }
    return null
  }

  const onChangeFile = (e, set_files, set_attchList, set_imgUrl) => {
    const files = Array.from(e.target.files)
    const file = files[0]
    // 파일 선택 창을 취소한 경우
    if (!file) {
      return
    }
    // accept='.png' 는 파일 선택 창 필터일 뿐이라 '모든 파일' 로 고른 경우를 막기 위해 형식 재확인
    if (file.type !== 'image/png') {
      showSnackbar('인장 파일은 배경이 투명한 PNG 만 등록할 수 있습니다.', theme.palette.warning.dark)
      e.target.value = ''
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      set_imgUrl(reader.result)
    }
    reader.readAsDataURL(file)
    set_files(files)
    set_attchList([])
    // 같은 파일을 제거 후 다시 선택해도 onChange 가 발생하도록 초기화
    e.target.value = ''
  }

  const onSave = () => {
    const msg = validate()
    if (msg) {
      showSnackbar(msg, theme.palette.warning.dark)
      return
    }
    onSubmit({
      ...form,
      branchName: form.branchName.trim(),
      companyName: (form.companyName ?? '').trim(),
      repName: (form.repName ?? '').trim(),
      bizNum1: form.bizNum1 ?? '',
      bizNum2: form.bizNum2 ?? '',
      bizNum3: form.bizNum3 ?? '',
      bizNum: `${form.bizNum1 ?? ''}${form.bizNum2 ?? ''}${form.bizNum3 ?? ''}`,
      addr2: (form.addr2 ?? '').trim(),
      phone: `${form.phoneDial}${form.phone1}${form.phone2}`,
      subLeaseYn: form.subLeaseYn ?? 'N',
      ownerName: (form.ownerName ?? '').trim(),
    }, [...attchList1, ...attchList2], files1, files2)
  }

  return (
    <>
      <AddrPopup
        ref={addrRef}
        title={'주소검색'}
        windowWidth='sm'
        callback={(data) => {
          // 선택 없이 닫은 경우에는 기존 주소 유지
          if (data.zonecode) {
            set_form((prev) => ({
              ...prev,
              zipCode: data.zonecode,
              addr1: getAddress(data, true, true),
            }))
          }
        }}
      />
      <Grid container spacing={2} style={{ marginTop: 5 }}>
        {isNew ? null : (
          <Grid item xs={12}>
            <Typography variant='body2'>지점번호: {form.id}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label='지점명'
            value={form.branchName ?? ''}
            inputProps={{ maxLength: 64 }}
            onChange={(e) => setField('branchName', e.target.value)}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label='회사명'
            value={form.companyName ?? ''}
            inputProps={{ maxLength: 64 }}
            onChange={(e) => setField('companyName', e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label='대표자명'
            value={form.repName ?? ''}
            inputProps={{ maxLength: 64 }}
            onChange={(e) => setField('repName', e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <NumericTextField
            fullWidth
            label='사업자번호 앞(3)'
            value={form.bizNum1 ?? ''}
            maxLength={3}
            onChange={(v) => setField('bizNum1', v)}
          />
        </Grid>
        <Grid item xs={3}>
          <NumericTextField
            fullWidth
            label='중간(2)'
            value={form.bizNum2 ?? ''}
            maxLength={2}
            onChange={(v) => setField('bizNum2', v)}
          />
        </Grid>
        <Grid item xs={5}>
          <NumericTextField
            fullWidth
            label='뒤(5)'
            value={form.bizNum3 ?? ''}
            maxLength={5}
            onChange={(v) => setField('bizNum3', v)}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField fullWidth label='우편번호' value={form.zipCode ?? ''} inputProps={{ readOnly: true }} />
        </Grid>
        <Grid item xs={8} style={{ display: 'flex', alignItems: 'center' }}>
          <Button onClick={() => addrRef.current.open()}>주소 찾기</Button>
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label='주소' value={form.addr1 ?? ''} inputProps={{ readOnly: true }} />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label='상세 주소'
            value={form.addr2 ?? ''}
            inputProps={{ maxLength: 64 }}
            onChange={(e) => setField('addr2', e.target.value)}
          />
        </Grid>

        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel id='phoneDial'>국번</InputLabel>
            <Select
              labelId='phoneDial'
              label='국번'
              value={form.phoneDial ?? ''}
              onChange={(e) => setField('phoneDial', e.target.value)}
            >
              <MenuItem value=''>선택 안 함</MenuItem>
              {dialCodes.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.code} ({c.name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label='앞 번호'
            value={form.phone1 ?? ''}
            inputProps={{ maxLength: 4 }}
            onChange={(e) => setField('phone1', onlyDigits(e.target.value))}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label='뒷 번호'
            value={form.phone2 ?? ''}
            inputProps={{ maxLength: 4 }}
            onChange={(e) => setField('phone2', onlyDigits(e.target.value))}
          />
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth required>
            <InputLabel id='statCode'>상태</InputLabel>
            <Select
              labelId='statCode'
              label='상태'
              value={form.statCode ?? ''}
              onChange={(e) => setField('statCode', e.target.value)}
            >
              {statCodes.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
          <Typography style={{ marginRight: 10 }}>노출:</Typography>
          <RadioGroup row value={form.displayYn ?? 'N'} onChange={(e) => setField('displayYn', e.target.value)}>
            {['Y', 'N'].map((yn) => (
              <FormControlLabel key={yn} value={yn} label={yn} control={<Radio size='small' />} />
            ))}
          </RadioGroup>
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography style={{ marginRight: 10 }}>본점:</Typography>
          <RadioGroup row value={form.hqYn ?? 'N'} onChange={(e) => setField('hqYn', e.target.value)}>
            {['Y', 'N'].map((yn) => (
              <FormControlLabel key={yn} value={yn} label={yn} control={<Radio size='small' />} />
            ))}
          </RadioGroup>
          <Typography variant='body2' color='text.secondary'>
            홈 화면 오시는 길 왼쪽에 표시됩니다.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <StampField
            label='지점 인장 파일'
            required={form.statCode === STAT_OPEN}
            helperText={`배경이 투명한 PNG, 60 x 60 픽셀 이상 권장${
              form.statCode === STAT_OPEN ? ', 영업 중인 지점은 필수 (계약서 임대인 인장)' : ''
            }`}
            files={files1}
            attchList={attchList1}
            imgUrl={imgUrl1}
            onChangeFile={(e) => onChangeFile(e, set_files1, set_attchList1, set_imgUrl1)}
            onClear={() => {
              set_files1([])
              set_attchList1([])
              set_imgUrl1(no_image)
            }}
          />
        </Grid>

        <Grid item xs={12} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography style={{ marginRight: 10 }}>전대:</Typography>
          <RadioGroup row value={form.subLeaseYn ?? 'N'} onChange={(e) => setField('subLeaseYn', e.target.value)}>
            {['Y', 'N'].map((yn) => (
              <FormControlLabel key={yn} value={yn} label={yn} control={<Radio size='small' />} />
            ))}
          </RadioGroup>
          <Typography variant='body2' color='text.secondary'>
            소유주와 임대인이 다른 경우 Y, 계약서에 소유주 동의란이 표시됩니다.
          </Typography>
        </Grid>
        {isSubLease ? (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label='소유주명'
                value={form.ownerName ?? ''}
                inputProps={{ maxLength: 64 }}
                onChange={(e) => setField('ownerName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <StampField
                label='소유주 인장 파일'
                required
                helperText='배경이 투명한 PNG, 60 x 60 픽셀 이상 권장, 전대 지점은 필수 (계약서 소유주 동의 인장)'
                files={files2}
                attchList={attchList2}
                imgUrl={imgUrl2}
                onChangeFile={(e) => onChangeFile(e, set_files2, set_attchList2, set_imgUrl2)}
                onClear={() => {
                  set_files2([])
                  set_attchList2([])
                  set_imgUrl2(no_image)
                }}
              />
            </Grid>
          </>
        ) : null}

        {isNew ? (
          <Grid item xs={12}>
            <Typography variant='body2' color='text.secondary'>
              신규 지점 등록 시 호실 100개가 함께 생성됩니다.
            </Typography>
          </Grid>
        ) : null}
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant='contained' onClick={onSave}>
            저장
          </Button>
        </Grid>
      </Grid>
    </>
  )
}

const BranchList = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const account = useSelector((state) => state.accountReducer)
  const codes = useSelector((state) => state.codeReducer)
  const statCodes = codes.filter((r) => r.codeGroupId === 'B001')
  const navigate = useNavigate()
  const location = useLocation()
  const pack = location.state?.pack
  const curLocationRef = useRef(location)
  const { alert, renderAlert } = useAlert()
  const [records, set_records] = useState([])
  const [totalCount, set_totalCount] = useState(0)
  const [pageInfo, set_pageInfo] = useState(pack?.data?.pageInfo ?? defPageInfo)
  // ref가 아닌 state로 관리: 다이얼로그 component prop이 선택된 지점으로 갱신되도록 함
  const [editTarget, set_editTarget] = useState(newBranch)
  const dgRef = useRef()
  const [openIndicator, closeIndicator, renderIndicator] = useIndicator()
  const fetchBranches = async () => {
    const url = `/branch/list`
    const resp = await WebService.get(url, {
      pageNum: pageInfo.pageNum,
      pageSize: pageInfo.pageSize,
    })

    if (resp.repCode !== 'ack') {
      alert(resp.repMessage)
      return
    }
    const repMessage = JSON.parse(resp.repMessage)
    const records = JSON.parse(repMessage.records)
    records.forEach((item) => {
      // AttchsRow 의 파일명 필드는 name (filename 필드는 없음)
      item.attchList = item.attchList.map((att) => ({
        ...att,
        name: att.name ?? att.filename,
      }))
      // 대표 이미지는 임대인 인장(SP01) 기준
      item.imgUrl = attchUrl(item.attchList.find((a) => a.catCode === CAT_STAMP1))
      console.log('item.imgUrl=>', item.imgUrl)
      // const uname = item.imgUrl ? item.imgUrl.split('/').pop() : ''
      // const path = uname ? item.imgUrl.substring(0, item.imgUrl.lastIndexOf('/')) : ''
      // item.imgUrl = item.imgUrl ? `/semu/repo/${path}/${encodeURIComponent(uname)}` : no_image
    })
    set_totalCount(+repMessage.totalCount)
    return records
  }

  const onLoad = async () => {
    const records = await fetchBranches()
    if (!records) {
      return
    }
    // id=-1 은 '호실 미배정' ghost 지점: 수정 시 id=-1 이 신규로 처리되므로 목록에서 제외
    set_records(records.filter((r) => r.id > 0))
  }

  const onOpenEdit = (branch) => {
    set_editTarget({ ...branch })
    dgRef.current.open()
  }

  const onSave = async (branch, attchList, files1, files2) => {
    const url = `/branch`
    const data = {
      branch: branch,
      oldAttchIdList: attchList.map((a) => a.id),
    }
    const formData = new FormData()
    // 서버가 파일명 접두어(stamp1_ / stamp2_)로 인장 용도를 구분하므로 접두어를 붙여 전송
    const appendStamp = (files, catCode) => {
      files.forEach((file) => {
        if (file instanceof File) {
          const f = new File([file], `${STAMP_PREFIX[catCode]}${file.name}`, { type: file.type })
          formData.append(`files`, f)
        }
      })
    }
    appendStamp(files1, CAT_STAMP1)
    appendStamp(files2, CAT_STAMP2)
    const params = {}
    const jsonData = JSON.stringify(data)
    formData.append('body', new Blob([jsonData], { type: 'application/json' }))

    openIndicator()
    const resp = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: params,
    })
    closeIndicator()

    //
    // const resp = await WebService.post(url, { branch })
    if (resp.data.repCode !== 'ack') {
      alert(resp.data.repMessage)
      return
    }
    dgRef.current.close()
    const isNew = branch.id === -1
    showSnackbar(isNew ? '등록되었습니다.' : '수정되었습니다.')
    // 목록이 id desc 정렬이므로 신규 등록 시 첫 페이지로 이동
    set_pageInfo(isNew ? { ...defPageInfo } : { ...pageInfo })
  }

  const onDelete = async (branchId) => {
    const url = `/branch/${branchId}`
    const resp = await WebService.delete(url)
    if (resp.repCode !== 'ack') {
      alert(resp.repMessage)
      return
    }
    showSnackbar('삭제되었습니다.')
    set_pageInfo({ ...pageInfo })
  }

  const onMoveRooms = (branch) => {
    navigate('/admin/branch/rooms', {
      state: {
        branch: branch,
        pack: {
          url: window.location.pathname,
          data: {
            pageInfo: pageInfo,
          },
        },
      },
    })
  }

  useEffect(() => {
    if (curLocationRef.current.key !== location.key) {
      curLocationRef.current = location
      onLoad()
    }
  }, [location])

  useEffect(() => {
    if (!account?.user?.id) {
      navigate('/login')
    }
    else {
      onLoad()
    }
  }, [pageInfo])

  return (
    <div
      style={{
        paddingTop: isMobile ? 50 : 30,
        paddingBottom: 50,
        minHeight: '75vh',
      }}
    >
      {renderAlert()}
      {renderIndicator()}
      <MyDialog2
        title={editTarget.id === -1 ? '지점등록' : '지점수정'}
        ref={dgRef}
        component={<BranchForm key={editTarget.id} branch={editTarget} onSubmit={onSave} />}
        windowWidth='md'
      />
      <Container>
        <Grid container spacing={2} style={{ alignItems: 'center' }}>
          <Grid
            item
            xs={12}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16 }}
          >
            <Typography style={boldFont} variant='h5'>
              지점목록
            </Typography>
            <Button variant='contained' onClick={() => onOpenEdit(newBranch)}>
              지점등록
            </Button>
          </Grid>

          {isMobile ? null : (
            <Grid item xs={12}>
              <Grid container spacing={1} style={{ alignItems: 'center' }}>
                <Grid item md={1}>
                  <Typography style={boldFont}>번호</Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography style={boldFont}>지점명</Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography style={boldFont}>사업자정보</Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography style={boldFont}>주소</Typography>
                </Grid>
                <Grid item md={1.5}>
                  <Typography style={boldFont}>전화</Typography>
                </Grid>
                <Grid item md={1}>
                  <Typography style={boldFont}>상태</Typography>
                </Grid>
                <Grid item md={0.8}>
                  <Typography style={boldFont}>노출</Typography>
                </Grid>
                <Grid item md={1.7}>
                  <Typography style={boldFont}>관리</Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>

          {records.length > 0 ? (
            records.map((item) => (
              <Grid item xs={12} key={item.id}>
                <Grid container spacing={1} style={{ alignItems: 'center' }}>
                  <Grid item xs={12} md={1}>
                    <MobileLabel label='[번호]' />
                    <Typography variant='body2'>{item.id}</Typography>
                  </Grid>
                  <Grid item xs={12} md={2} style={{ cursor: 'pointer' }} onClick={() => onMoveRooms(item)}>
                    <MobileLabel label='[지점명]' />
                    <Typography variant='body2' style={{ fontWeight: 'bold' }}>
                      {item.branchName}
                      {item.hqYn === 'Y' ? (
                        <span style={{ marginLeft: 6, fontWeight: 'normal', color: theme.palette.primary.main }}>
                          (본점)
                        </span>
                      ) : null}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <MobileLabel label='[사업자정보]' />
                    <Typography variant='body2'>{item.companyName}</Typography>
                    <Typography variant='body2'>{item.repName}</Typography>
                    <Typography variant='body2'>{formatBizNum(item)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <MobileLabel label='[주소]' />
                    <Typography variant='body2'>{item.zipCode ? `(${item.zipCode})` : ''}</Typography>
                    <Typography variant='body2'>
                      {item.addr1} {item.addr2}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <MobileLabel label='[전화]' />
                    <Typography variant='body2'>{formatPhone(item)}</Typography>
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <MobileLabel label='[상태]' />
                    <Typography variant='body2'>
                      {statCodes.find((c) => c.code === item.statCode)?.name ?? item.statCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={0.8}>
                    <MobileLabel label='[노출]' />
                    <Typography variant='body2'>
                      {item.displayYn}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={1.7} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <FlatButton size='small' onClick={() => onOpenEdit(item)} style={{ marginRight: 5 }}>
                      수정
                    </FlatButton>
                    <ConfirmDialog
                      btn={<FlatButton size='small' color={'error'}>삭제</FlatButton>}
                      title={'주의'}
                      content={`[${item.branchName}] 지점을 삭제하시겠습니까?\n지점의 모든 호실이 삭제되고, 연결된 계약의 호실 배정이 해제됩니다.`}
                      bc={'error'}
                      perform={() => {
                        onDelete(item.id)
                      }}
                    />
                  </Grid>
                </Grid>
                <Divider style={{ marginTop: 8 }} />
              </Grid>
            ))
          ) : (
            // <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
            //   <Typography color='text.secondary'>등록된 지점이 없습니다.</Typography>
            // </Grid>
            null
          )}

          <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <MyPagination totalCount={totalCount} pageInfo={pageInfo} set_pageInfo={set_pageInfo} />
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

export default BranchList
